import { makeModuleStateSelector } from "@client/content/selectors";
import { pick } from "@client/lib/object";
import { getSettingValue } from "@client/lib/settings";
import {
  TargetSelector,
  combinedSettingsSelector,
  entitiesSelector,
  makeEntityChildrenIdsSelector,
  makeEntityChildrenSelector,
  makeEntitySettingsSelector
} from "@client/selectors";
import { type GenericMap, ScreenType } from "@client/types";
import { createSelector } from "reselect";
import { defaultState, previewState } from "./reducer";
import { type ReportModule } from "./types";

export const localStateSelector = makeModuleStateSelector<ReportModule.State>(defaultState, previewState);

export const makeLocalStateItemIdSelector = () => {
  const entityChildrenIdsSelector = makeEntityChildrenIdsSelector();

  return createSelector([localStateSelector, entityChildrenIdsSelector], (localState, childrenIds) =>
    localState.itemId && childrenIds.includes(localState.itemId) ? localState.itemId : childrenIds[0] ?? null
  );
};

export const makeLocalStateItemSelector = () => {
  const itemIdSelector = makeLocalStateItemIdSelector();

  return createSelector([itemIdSelector, entitiesSelector], (itemId, entities) => (itemId ? entities[itemId] : null));
};

const makeLocalStateReportOptionsSelector = () => {
  return createSelector([localStateSelector], (localState) => localState.reportOptions);
};

export const makeReportOptionsSelector = () => {
  const entityChildrenSelector = makeEntityChildrenSelector();
  const localStateReportOptionsSelector = makeLocalStateReportOptionsSelector();

  return createSelector(
    [localStateReportOptionsSelector, entityChildrenSelector],
    (lsReportOptions, children): ReportModule.ReportOptionItem[] => {
      const lsoMap = lsReportOptions.reduce(
        (res, x) => ({
          ...res,
          [x.value]: x.label
        }),
        {}
      );

      return children
        .map<ReportModule.ReportOptionItem>((x) => ({
          reportId: (x.data as ReportModule.ModuleItemData).reportId,
          label: x.settings!["caption"],
          value: x.id
        }))
        .filter((x) => x.reportId in lsoMap)
        .map((x) => ({
          ...x,
          label: x.label || lsoMap[x.reportId]
        }));
    }
  );
};

export const makeReportIdSelector = () => {
  const itemSelector = makeLocalStateItemSelector();

  return createSelector([itemSelector], (item): string | null => {
    if (!item) return null;

    return (item.data as ReportModule.ModuleItemData).reportId;
  });
};

export const makeCustomsSelector = () => {
  const settingsSelector = makeEntitySettingsSelector();

  return createSelector([settingsSelector], (settings): GenericMap<any> => {
    let paramSettings = pick(settings, [/^\p_/]);

    return Object.keys(paramSettings)
      .filter((x) => paramSettings[`${x}Default`] === "Custom")
      .reduce(
        (res, x) => ({
          ...res,
          [x.replace(/^p_/, "")]: getSettingValue(paramSettings, "", x, ScreenType.Desktop)
        }),
        {}
      );
  });
};

export const makeModuleReportSettingsSelector = () => {
  const customsSelector = makeCustomsSelector();
  const settingsSelector = combinedSettingsSelector(TargetSelector.Entity, ["includeGrid", "filterVisibility"]);

  return createSelector([customsSelector, settingsSelector], (customs, settings): ReportModule.Settings => {
    return {
      customs,
      includeGrid: settings.includeGrid,
      visible: settings.filterVisibility ? settings.filterVisibility.split(",") : null
    };
  });
};
